import { Box, styled, Typography } from '@mui/material';

import { memo, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AlertSnackbarContext } from '../../components/snackbars';
import { Spinner } from '../../components/spinners';
import { AlertMessages, Color, ErrorMessageStrings, GlobalStrings } from '../../constants';
import { SignUpToEventDto, useGetEventById, useSignupEvent } from '../../lib/event/hooks';
import { useRequestOtp } from '../../lib/user/hooks/useRequestOtp';
import { useSignUpFormStore } from '../hooks/persist/useSignUpFormStore';
import VerifyOtp from '../verify-otp/VerifyOtp';

import SignUpForm from './sign-up/SignUpForm';

export type UserSignUpFormValues = {
  fullname: string;
  email: string;
  phone: string;
};

const EventSignUp = () => {
  const { id } = useParams();
  const { data: event, isLoading } = useGetEventById(id || '');
  const { mutate } = useRequestOtp();
  const [, setSnackbar] = useContext(AlertSnackbarContext);
  const eventKey = id || 'default';
  const { valuesByEventId, setValues, clearValues, isSignUpByEventId, setIsSignUp } = useSignUpFormStore();
  const storedValues = valuesByEventId[eventKey] || {};
  const initialValues = useMemo<Partial<UserSignUpFormValues>>(
    () => ({
      fullname: '',
      phone: '',
      email: '',
      ...storedValues,
    }),
    [storedValues],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = searchParams.get('step') || 'signup';
  const isSignUpFromParams = searchParams.get('isSignUp') !== 'false';
  const isSignUp = isSignUpByEventId[eventKey] ?? isSignUpFromParams;
  const { mutate: signUpToEvent } = useSignupEvent();
  const handleSignUp = useCallback(
    () =>
      signUpToEvent({ eventId: id } as SignUpToEventDto, {
        onSuccess: () => {
          navigate(`/discover/${id}`);
        },
      }),
    [],
  );
  const navigate = useNavigate();
  const setStep = (step: 'signup' | 'verify') => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('step', step);
      return newParams;
    });
  };

  const setIsSignUpFlag = (value: boolean) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('isSignUp', String(value));
      return newParams;
    });
    setIsSignUp(eventKey, value);
  };

  const requestOtp = (phone: string, isSignUp: boolean) => {
    setIsSignUpFlag(isSignUp);
    mutate(
      { phone: phone, isSignUp: isSignUp },
      {
        onSuccess: () => {
          setSnackbar({
            message: AlertMessages.VERIFICATION_CODE_SENT_SUCCESS,
            severity: 'success',
          });
          setStep('verify');
        },
        onError: (error: Error) => {
          if (error.message.toLowerCase().includes(ErrorMessageStrings.DUPLICATE_PHONE)) {
            setIsSignUpFlag(false);
            requestOtp(phone, false);
          } else {
            setSnackbar({
              message: error.message,
              severity: 'error',
            });
          }
        },
      },
    );
  };

  const handleSubmit = (values: Partial<UserSignUpFormValues>) => {
    setValues(eventKey, values);
    requestOtp(values.phone || '', true);
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!event) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography color="error">Event not found.</Typography>
      </Box>
    );
  }

  return (
    <Container>
      <LogoContainer>
        <img src="/assets/images/logo.svg" alt={GlobalStrings.GLOBAL_PROJECT_NAME} />
      </LogoContainer>

      {currentStep === 'signup' ? (
        <SignUpForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onValuesChange={(values) => {
            setValues(eventKey, values);
          }}
        />
      ) : (
        <VerifyOtp
          event={event}
          isSignUp={isSignUp}
          formValue={{
            fullname: storedValues.fullname || '',
            email: storedValues.email || '',
            phone: storedValues.phone || '',
          }}
          onFinish={() => {
            clearValues(eventKey);
            if (event?.entryFee > 0) {
              navigate(`/discover/${id}`, { state: { signUpUser: true } });
            } else {
              handleSignUp();
            }
          }}
        />
      )}
    </Container>
  );
};

export default memo(EventSignUp);

const Container = styled('div')({
  background: Color.cultured,
  borderRadius: '16px',
  padding: '24px',
  width: '100vw',
  height: '100vh',
});

const LogoContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  borderRadius: 8,
  backgroundColor: Color.cultured,
  marginBottom: 18,
});
